import React, { useEffect } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { getUsers } from '../../actions/userActions';

ChartJS.register(ArcElement, Tooltip, Legend);

const AdminDashboardChart = ({ getUsers, users }) => {
  useEffect(() => {
    getUsers();
  }, [getUsers]);

  const roleCounts = users.reduce((acc, user) => {
    acc[user.role] = (acc[user.role] || 0) + 1;
    return acc;
  }, {});

  const totalUsers = users.length;

  const data = {
    labels: Object.keys(roleCounts).map(role => role.charAt(0).toUpperCase() + role.slice(1)),
    datasets: [
      {
        data: Object.values(roleCounts),
        backgroundColor: [
          '#054581', // Secondary color for admin
          '#1086a4', // Primary color for instructor
          '#38bdf8', // Lighter blue for student
          '#94a3b8'  // Gray for visitor
        ],
        borderWidth: 0,
        cutout: '70%',
      },
    ],
  };

  const options = {
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const percentage = ((context.parsed / totalUsers) * 100).toFixed(1);
            return `${context.label}: ${context.parsed} (${percentage}%)`;
          }
        },
        backgroundColor: '#054581',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        cornerRadius: 8
      }
    },
    maintainAspectRatio: false,
  };

  return (
    <div className="bg-[#f9fafb] rounded-xl p-6 flex flex-col md:flex-row items-center justify-between w-full max-w-2xl mx-auto">
      <div className="flex flex-col items-start mb-4 md:mb-0">
        <span className="text-[#054581] font-medium">Total Users</span>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-3xl font-bold text-[#1086a4]">{totalUsers}</span>
        </div>
      </div>
      <div className="flex items-center gap-8 w-full md:w-auto justify-center">
        <div className="relative w-40 h-40">
          <Doughnut data={data} options={options} />
        </div>
        <div className="flex flex-col gap-3 text-sm">
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#054581' }}></span>
            <span className="text-gray-700">Admin ({roleCounts.admin || 0})</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#1086a4' }}></span>
            <span className="text-gray-700">Instructor ({roleCounts.instructor || 0})</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#38bdf8' }}></span>
            <span className="text-gray-700">Student ({roleCounts.student || 0})</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#94a3b8' }}></span>
            <span className="text-gray-700">Visitor ({roleCounts.visitor || 0})</span>
          </div>
        </div>
      </div>
    </div>
  );
};

AdminDashboardChart.propTypes = {
  getUsers: PropTypes.func.isRequired,
  users: PropTypes.array.isRequired
};

const mapStateToProps = state => ({
  users: state.user.users
});

export default connect(mapStateToProps, { getUsers })(AdminDashboardChart); 
